import { useState } from 'react';

import { useDemoContext } from '@/hooks/DemoContext';
import { useAuth } from '@/hooks/useAuth';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import buildClickEvent from '@/utils/functions/buildClickEvent/buildClickEvent';
import getVideoTutorials from '@/utils/functions/getVideoTutorials/getVideoTutorials';
import readPartnerTutorialsPreference from '@/utils/functions/readPartnerTutorialsPreference/readPartnerTutorialsPreference';
import trackTutorialPlay from '@/utils/functions/trackTutorialPlay/trackTutorialPlay';
import writePartnerTutorialsPreference from '@/utils/functions/writePartnerTutorialsPreference/writePartnerTutorialsPreference';

import type { PartnerTrackingContext, TutorielVideo } from '@/types/videoTutorials';
import type { KeyboardEvent, MouseEvent } from 'react';

interface UsePartnerCardTutorialsParams {
  assureur: string;
  link?: string;
  trackingContext?: PartnerTrackingContext;
}

const usePartnerCardTutorials = ({ assureur, link, trackingContext }: UsePartnerCardTutorialsParams) => {
  const { user } = useAuth();
  const { isDemo } = useDemoContext();
  const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);
  const [isInterstitialOpen, setIsInterstitialOpen] = useState(false);

  const { catalogKey, tutorials } = getVideoTutorials(link);
  const hasTutorials = tutorials.length > 0;
  // Les vidéos filment le portail de l'assureur du catalogue, pas celui du libellé API (ex. « UAF Life » sur le lien Spirica).
  const partnerName = catalogKey ?? '';

  const handleTracking = (eventNom: string, eventDetail: string, eventDetail2 = assureur) => {
    if (isDemo) return;

    void void TRACKING_INSTANCE.triggerEvent(
      'clic',
      undefined,
      buildClickEvent(
        { event_nom: eventNom, event_detail_1: eventDetail, event_detail_2: eventDetail2 },
        trackingContext
      )
    );
  };

  const trackPartnerAccess = () => handleTracking('acceder a mon espace partenaire', 'lien textuel');

  const trackTutorialsModalOpening = () => handleTracking('voir les tutoriels', 'modale');

  // La préférence est relue au clic : une case cochée depuis une autre carte déjà montée doit compter.
  const shouldShowInterstitial = () => !!user && !isDemo && hasTutorials && !readPartnerTutorialsPreference();

  const openInterstitial = () => {
    setIsInterstitialOpen(true);
    handleTracking('avant redirection partenaire', 'interstitiel');
  };

  const openTutorialsModal = () => {
    setIsTutorialsModalOpen(true);
    trackTutorialsModalOpening();
  };

  const closeTutorialsModal = () => setIsTutorialsModalOpen(false);

  const closeInterstitial = () => setIsInterstitialOpen(false);

  const handlePartnerLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (shouldShowInterstitial()) {
      event.preventDefault();
      openInterstitial();
      return;
    }
    trackPartnerAccess();
  };

  const handlePartnerLinkKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (isDemo || !link) return;
    if (shouldShowInterstitial()) {
      openInterstitial();
      return;
    }
    trackPartnerAccess();
    globalThis.open(link, '_blank', 'noopener,noreferrer');
  };

  const handlePlay = (tutorial: TutorielVideo) => {
    if (isDemo) return;
    trackTutorialPlay(partnerName, tutorial, trackingContext);
  };

  const handleModalPartnerAccess = () => {
    if (!link) return;
    trackPartnerAccess();
    globalThis.open(link, '_blank', 'noopener,noreferrer');
  };

  const handleHidePartnerTutorials = () => writePartnerTutorialsPreference(true);

  const handleHideCheckboxChange = (checked: boolean) => {
    if (checked) handleTracking('avant redirection partenaire', 'ne plus afficher');
  };

  const handleInterstitialPartnerAccessPress = () => {
    trackPartnerAccess();
    closeInterstitial();
  };

  const handleTrackInfo = () => handleTracking('informations partenaire', 'picto');

  return {
    tutorials,
    partnerName,
    hasTutorials,
    isDemo,
    isTutorialsModalOpen,
    isInterstitialOpen,
    openTutorialsModal,
    closeTutorialsModal,
    closeInterstitial,
    handlePartnerLinkClick,
    handlePartnerLinkKeyDown,
    handlePlay,
    handleHidePartnerTutorials,
    handleHideCheckboxChange,
    handleModalPartnerAccess,
    handleInterstitialPartnerAccessPress,
    handleOpenPlaybackFromInterstitial: trackTutorialsModalOpening,
    handleTrackInfo,
  };
};

export default usePartnerCardTutorials;
